import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { apiBffFetch, apiChatFetch } from '..';
import {
  CHAT_QUERY_KEYS,
  type ChatIdentityKey,
  chatQueryKey,
  chatQueryKeyIdentity,
} from './query-keys';
import type { GetRecipientsParams, GetRecipientsRes } from './types';
import type {
  ContextIdParams,
  ContextSummary,
  GetContextMessagesQueryParams,
  GetContextMessagesRes,
  GetContextsParams,
  GetContextsRes,
  GetContextThreadsParams,
  GetContextThreadsRes,
  GetThreadParticipantsParams,
  GetThreadParticipantsRes,
  GetTopicsRes,
} from './v2-types';

export const _getRecipients = (identity: ChatIdentityKey, params?: GetRecipientsParams) => {
  return queryOptions({
    queryKey: chatQueryKey(identity, CHAT_QUERY_KEYS.recipient, params),
    queryFn: () => apiChatFetch<GetRecipientsRes>('/recipients', { params }),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};

export const RECIPIENTS_PAGE_SIZE = 50;

const RECIPIENTS_SERVER_CACHE_TTL_MS = 30_000;

export const RECIPIENTS_STALE_TIME_MS = RECIPIENTS_SERVER_CACHE_TTL_MS;

export const _getRecipientsInfinite = (
  identity: ChatIdentityKey,
  params: Omit<GetRecipientsParams, 'offset'> = {},
) => {
  const limit = params.limit ?? RECIPIENTS_PAGE_SIZE;

  return infiniteQueryOptions({
    queryKey: chatQueryKey(identity, CHAT_QUERY_KEYS.recipient, 'infinite', params),
    queryFn: ({ pageParam, signal }) =>
      apiChatFetch<GetRecipientsRes>('/recipients', {
        params: { ...params, limit, offset: pageParam },
        signal,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore
        ? lastPage.pagination.offset + lastPage.pagination.limit
        : undefined,
    staleTime: RECIPIENTS_STALE_TIME_MS,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};

export const _getRecipientById = (identity: ChatIdentityKey, recipientId: string | null) => {
  return queryOptions({
    queryKey: chatQueryKey(identity, CHAT_QUERY_KEYS.recipient, 'by-id', recipientId),
    queryFn: ({ signal }) =>
      apiChatFetch<GetRecipientsRes>('/recipients', {
        params: { recipientId: recipientId ?? undefined, limit: 1 },
        signal,
      }),
    enabled: !!recipientId,
    staleTime: RECIPIENTS_STALE_TIME_MS,
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};

export const _getRecipientsEntities = (identity: ChatIdentityKey, params?: GetRecipientsParams) => {
  return queryOptions({
    queryKey: chatQueryKey(identity, CHAT_QUERY_KEYS.recipientEntities, params),
    queryFn: () => apiChatFetch<GetRecipientsRes>('/recipients/entities', { params }),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};

export const CONTEXTS_PAGE_SIZE = 50;

export const _getContexts = (
  identity: ChatIdentityKey,
  params: Omit<GetContextsParams, 'offset'> = {},
) => {
  const limit = params.limit ?? CONTEXTS_PAGE_SIZE;
  const hasSubItems = Boolean(params.categoryNodeIds?.length || params.topicIds?.length);

  return infiniteQueryOptions({
    queryKey: chatQueryKey(identity, CHAT_QUERY_KEYS.contexts, params),
    queryFn: ({ pageParam, signal }) =>
      (hasSubItems ? apiBffFetch : apiChatFetch)<GetContextsRes>(
        hasSubItems ? '/communications/contexts' : '/contexts',
        {
          params: { ...params, limit, offset: pageParam },
          signal,
        },
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore
        ? lastPage.pagination.offset + lastPage.pagination.limit
        : undefined,
    placeholderData: (previous, previousQuery) =>
      previousQuery && chatQueryKeyIdentity(previousQuery.queryKey) === identity
        ? previous
        : undefined,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};

export const _getContextThreads = (
  identity: ChatIdentityKey,
  { contextId, ...params }: GetContextThreadsParams,
) => {
  const requestParams = {
    ...params,
    includeResolved: params.includeResolved ?? false,
    includeArchived: params.includeArchived || params.includeResolved === true,
  };

  return queryOptions({
    queryKey: chatQueryKey(identity, CHAT_QUERY_KEYS.contextThreads, contextId, requestParams),
    queryFn: ({ signal }) =>
      apiChatFetch<GetContextThreadsRes>(`/contexts/${contextId}/threads`, {
        params: requestParams,
        signal,
      }),
    enabled: !!contextId,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};

export const CONTEXT_THREADS_PAGE_SIZE = 50;

export const _getContextThreadsInfinite = (
  identity: ChatIdentityKey,
  { contextId, ...params }: Omit<GetContextThreadsParams, 'offset'>,
) => {
  const requestParams = {
    ...params,
    limit: params.limit ?? CONTEXT_THREADS_PAGE_SIZE,
    includeResolved: params.includeResolved ?? false,
    includeArchived: params.includeArchived || params.includeResolved === true,
  };

  return infiniteQueryOptions({
    queryKey: chatQueryKey(identity, CHAT_QUERY_KEYS.contextThreadsPaged, contextId, requestParams),
    queryFn: ({ pageParam, signal }) =>
      apiChatFetch<GetContextThreadsRes>(`/contexts/${contextId}/threads`, {
        params: { ...requestParams, offset: pageParam },
        signal,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore
        ? lastPage.pagination.offset + lastPage.pagination.limit
        : undefined,
    enabled: !!contextId,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};

export const _getContextMessages = (
  identity: ChatIdentityKey,
  { contextId, ...params }: GetContextMessagesQueryParams,
) => {
  return infiniteQueryOptions({
    queryKey: chatQueryKey(identity, CHAT_QUERY_KEYS.contextMessages, contextId, params),
    queryFn: ({ pageParam, signal }) =>
      apiChatFetch<GetContextMessagesRes>(`/contexts/${contextId}/messages`, {
        params: {
          ...params,
          ...(pageParam === undefined ? {} : { before: pageParam }),
        },
        signal,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? (lastPage.nextCursor ?? undefined) : undefined,
    enabled: !!contextId,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};

export const _getContextSummary = (identity: ChatIdentityKey, { contextId }: ContextIdParams) => {
  return queryOptions({
    queryKey: chatQueryKey(identity, CHAT_QUERY_KEYS.contextSummary, contextId),
    queryFn: ({ signal }) =>
      apiChatFetch<ContextSummary>(`/contexts/${contextId}/summary`, { signal }),
    enabled: !!contextId,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};

export const _getTopics = (identity: ChatIdentityKey) => {
  return queryOptions({
    queryKey: chatQueryKey(identity, CHAT_QUERY_KEYS.topics),
    queryFn: ({ signal }) => apiChatFetch<GetTopicsRes>('/topics', { signal }),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};

export const _getThreadParticipants = (
  identity: ChatIdentityKey,
  { threadId, ...params }: GetThreadParticipantsParams,
) => {
  return queryOptions({
    queryKey: chatQueryKey(identity, CHAT_QUERY_KEYS.participants, threadId, params),
    queryFn: ({ signal }) =>
      apiChatFetch<GetThreadParticipantsRes>(`/threads/${threadId}/participants`, {
        params,
        signal,
      }),
    enabled: !!threadId,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};
